import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { type LucideIcon, Plus, Trash2 } from "lucide-react"
import * as React from "react"

import { LoadingSpinner } from "components/ui/loading-spinner"
import { cn } from "lib/utils"

const buttonVariants = cva(
  "inline-flex justify-center items-center gap-2 disabled:opacity-70 rounded-md focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-slate-950 font-sans font-medium text-sm whitespace-nowrap transition-colors disabled:pointer-events-none",
  {
    variants: {
      variant: {
        default: "bg-slate-900 text-slate-50 hover:bg-slate-700",
        primary:
          "bg-foreground text-white hover:bg-foreground/80 disabled:opacity-100 disabled:bg-secondary disabled:cursor-not-allowed disabled:text-base-foreground",
        destructive: "bg-red-500 text-slate-50 hover:bg-red-500/90",
        outline:
          "border border-border bg-white hover:bg-zinc-100 text-dark-text hover:text-zinc-950 shadow-sm",
        secondary: "bg-slate-100 text-slate-900 hover:bg-slate-300",
        ghost: "hover:bg-zinc-100 hover:text-zinc-900",
        link: "text-slate-900 underline-offset-4 hover:underline",
      },
      size: {
        auto: "h-auto py-1 px-2",
        default: "h-9 px-4 py-2",
        sm: "h-8 rounded-md px-3 text-xs gap-1",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

const iconMap = {
  create: Plus,
  delete: Trash2,
}

const ButtonIcon = ({
  action,
  icon,
  iconClassName,
  isLoading,
}: Pick<ButtonProps, "action" | "icon" | "iconClassName" | "isLoading">) => {
  return React.useMemo(() => {
    if (isLoading) {
      return (
        <LoadingSpinner
          className="w-4 h-4 text-muted-foreground animate-spin"
          aria-hidden="true"
        />
      )
    }

    const Icon = action ? iconMap[action] : icon

    if (Icon) {
      return <Icon className={cn("w-4 h-4", iconClassName)} />
    }

    return null
  }, [action, icon, iconClassName, isLoading])
}

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
  action?: keyof typeof iconMap
  icon?: LucideIcon
  iconClassName?: string
  isLoading?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      action,
      isLoading,
      icon,
      iconClassName,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "button"

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={isLoading}
        {...props}
      >
        <ButtonIcon
          action={action}
          icon={icon}
          iconClassName={iconClassName}
          isLoading={isLoading}
        />
        {children}
      </Comp>
    )
  }
)

Button.displayName = "Button"

export { Button, buttonVariants }
